import { Component, computed, inject, input, OnInit } from '@angular/core';
import { OrganizationSidebarLayout } from '../../../organizations/components/organization-sidebar-layout/organization-sidebar-layout';
import { PageHeader } from '../../../layout/components/page-header/page-header';
import {
  Button,
  BreadcrumbNav,
  Breadcrumb,
  Skeleton,
  SwitchBar,
  SwitchBarOption,
} from 'excellence-ui';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgIcon } from '@ng-icons/core';
import { PageTitle } from '../../../layout/components/page-title/page-title';
import { ErrorComponent } from '../../../../shared/components/error-component/error-component';
import { NgTemplateOutlet } from '@angular/common';
import { GermanNumberPipe } from '../../../../shared/pipes/german-number-pipe';
import { TranslocoPipe } from '@jsverse/transloco';
import { format, setYear } from 'date-fns';
import { AnalysisPriceGraphChart } from '../../components/analysis-price-graph-chart/analysis-price-graph-chart';
import { AnalysisPriceFilters } from '../../components/analysis-price-filters/analysis-price-filters';
import { getFilterFromQueryParams } from '../../components/analysis-price-filters/definitions';
import { ConvertPricePipe } from '../../pipes/convert-price-pipe';
import { PriceFilter, PriceStore } from '../../stores/price.store';
import { form, FormField } from '@angular/forms/signals';
import { AnalysisPriceTable } from '../../components/analysis-price-table/analysis-price-table';
import { AnalysisPriceComparisonSelect } from '../../components/analysis-price-comparison-select/analysis-price-comparison-select';

@Component({
  selector: 'app-analysis-price-page',
  templateUrl: './analysis-price-page.html',
  imports: [
    OrganizationSidebarLayout,
    PageHeader,
    Button,
    RouterLink,
    NgIcon,
    PageTitle,
    BreadcrumbNav,
    Breadcrumb,
    ErrorComponent,
    Skeleton,
    NgTemplateOutlet,
    GermanNumberPipe,
    TranslocoPipe,
    AnalysisPriceGraphChart,
    AnalysisPriceFilters,
    ConvertPricePipe,
    SwitchBar,
    SwitchBarOption,
    FormField,
    AnalysisPriceTable,
    AnalysisPriceComparisonSelect,
  ],
})
export class AnalysisPricePage implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private priceStore = inject(PriceStore);

  organizationId = input.required<string>();

  protected filter = this.priceStore.filter;
  protected filterForm = form(this.priceStore.filter);
  protected data = computed(() => this.priceStore.get(this.filter()));
  protected comparisonData = computed(() => {
    const year = this.filter().compareHistoricYear;
    if (!year) return null;
    return this.priceStore.get({
      ...this.filter(),
      date: setYear(this.filter().date, Number(year)),
      compareHistoricYear: '',
    });
  });
  protected comparisonPrices = computed(() => this.comparisonData()?.data?.entries ?? null);
  protected granularity = computed(() => this.filter().granularity);
  protected date = computed(() => this.filter().date);
  protected showEurMwh = computed(() => this.filter().showEurMwh);

  ngOnInit(): void {
    this.initFilter();
  }

  private initFilter() {
    this.priceStore.filter.set(getFilterFromQueryParams(this.activatedRoute.snapshot));
    this.priceStore.onChangeFilter();
  }

  protected onChangeFilter(filter: PriceFilter) {
    this.priceStore.filter.set(filter);
    this.replaceQueryParams(filter);
    this.priceStore.onChangeFilter();
  }

  protected onChangeView(view: unknown) {
    if (view !== 'table' && view !== 'graph') {
      throw new Error(`Invalid view value: ${view}`);
    }
    this.onChangeFilter({
      ...this.priceStore.filter(),
      view,
    });
  }

  protected onChangeHistoricYear(year: string | null) {
    this.onChangeFilter({
      ...this.priceStore.filter(),
      compareHistoricYear: year === null ? '' : year,
    });
  }

  private replaceQueryParams(filter: PriceFilter) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        granularity: filter.granularity || null,
        date: format(filter.date, 'yyyy-MM-dd'),
        showEurMwh: filter.showEurMwh ? 'true' : null,
        view: filter.view,
        compareHistoricYear: filter.compareHistoricYear || null,
      },
      queryParamsHandling: 'replace',
    });
  }

  protected onClickReload() {
    this.priceStore.load(this.priceStore.filter());
  }
}
